import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from "@tanstack/react-query";
import { useMemo, useCallback } from "react";
import {
  DomainContextAPIService,
  DomainsAPIService,
  type GetDomainContextResponse,
  type CreateDomainResponse,
} from "@rails_api_base";
import { useWebsiteId } from "~/stores/projectStore";
import { useJwt, useRootPath } from "~/stores/sessionStore";
import { useLatestMutation } from "~/hooks/useLatestMutation";
import { isPlatformDomain } from "~/lib/domain";

// ============================================================================
// Query Keys
// ============================================================================

export const domainContextKeys = {
  all: ["domainContext"] as const,
  context: (websiteId: number) => [...domainContextKeys.all, "context", websiteId] as const,
};

// ============================================================================
// Service Hooks
// ============================================================================

export function useDomainContextService() {
  const jwt = useJwt();
  const rootPath = useRootPath();
  return useMemo(
    () => new DomainContextAPIService({ jwt: jwt ?? "", baseUrl: rootPath ?? "" }),
    [jwt, rootPath]
  );
}

export function useDomainsService() {
  const jwt = useJwt();
  const rootPath = useRootPath();
  return useMemo(
    () => new DomainsAPIService({ jwt: jwt ?? "", baseUrl: rootPath ?? "" }),
    [jwt, rootPath]
  );
}

// ============================================================================
// Query Hooks
// ============================================================================

type DomainContextQueryOptions = Omit<
  UseQueryOptions<GetDomainContextResponse, Error>,
  "queryKey" | "queryFn"
>;

/**
 * Hook for fetching domain context from Rails API.
 * Returns existing domains, subdomain credits, and brainstorm context.
 *
 * For AI-generated domain recommendations, use useWebsiteChat().state.domainRecommendations
 * which comes from the website graph.
 */
export function useDomainContext(websiteId?: number, options?: DomainContextQueryOptions) {
  const service = useDomainContextService();
  const currentWebsiteId = useWebsiteId();
  const effectiveWebsiteId = websiteId ?? currentWebsiteId;

  return useQuery({
    queryKey: domainContextKeys.context(effectiveWebsiteId ?? 0),
    queryFn: () => service.get(effectiveWebsiteId!),
    enabled: !!effectiveWebsiteId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export interface CreateDomainVariables {
  domain: string;
  websiteId: number;
  isPlatformSubdomain?: boolean;
}

export interface UpdateDomainVariables {
  domainId: number;
  websiteId: number;
}

type CreateDomainMutationOptions = Omit<
  UseMutationOptions<CreateDomainResponse, Error, CreateDomainVariables>,
  "mutationFn"
>;

/**
 * Hook for creating a new domain and assigning it to a website.
 * Updates domain context cache with new credits and assigned_url on success.
 * The optimistic assigned_url update ensures back button navigation shows the new assignment.
 */
export function useCreateDomain(options?: CreateDomainMutationOptions) {
  const service = useDomainsService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ domain, websiteId, isPlatformSubdomain }: CreateDomainVariables) => {
      return service.create({
        domain,
        website_id: websiteId,
        is_platform_subdomain: isPlatformSubdomain ?? isPlatformDomain(domain),
      } as Parameters<typeof service.create>[0]);
    },
    onSuccess: (data, variables) => {
      const queryKey = domainContextKeys.context(variables.websiteId);

      // Update cache immediately with new credits and assigned URL from response
      // This prevents stale credit counts and ensures back button shows new assignment
      queryClient.setQueryData<GetDomainContextResponse>(queryKey, (oldData) => {
        if (!oldData) return oldData;

        const isPlatformSubdomain =
          variables.isPlatformSubdomain ?? isPlatformDomain(variables.domain);

        return {
          ...oldData,
          platform_subdomain_credits:
            data.platform_subdomain_credits as GetDomainContextResponse["platform_subdomain_credits"],
          // Domain ↔ Website is 1:1; the assigned_url is derived from the
          // Domain itself. Paths are always "/".
          assigned_url: {
            id: data.domain.id,
            domain_id: data.domain.id,
            domain: data.domain.domain,
            path: "/",
            is_platform_subdomain: isPlatformSubdomain,
            dns_verification_status: isPlatformSubdomain ? "verified" : "pending",
            full_url: data.domain.full_url,
          },
        };
      });

      // Also invalidate to ensure full refresh on next access
      queryClient.invalidateQueries({ queryKey });
    },
    ...options,
  });
}

type UpdateDomainMutationOptions = Omit<
  UseMutationOptions<unknown, Error, UpdateDomainVariables>,
  "mutationFn"
>;

/**
 * Hook for reassigning an existing domain to a different website.
 * Invalidates domain context cache on success.
 */
export function useUpdateDomain(options?: UpdateDomainMutationOptions) {
  const service = useDomainsService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ domainId, websiteId }: UpdateDomainVariables) => {
      // website_id is accepted by the API but not in the generated OpenAPI types
      return service.update(domainId, {
        website_id: websiteId,
      } as any);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: domainContextKeys.context(variables.websiteId),
      });
    },
    ...options,
  });
}

export interface ReleaseDomainVariables {
  domainId: number;
  /** The website whose domain context should refresh (existing domains + credits). */
  websiteId: number;
}

type ReleaseDomainMutationOptions = Omit<
  UseMutationOptions<void, Error, ReleaseDomainVariables>,
  "mutationFn"
>;

/**
 * Hook for releasing a domain claim entirely (hard delete). Frees the
 * platform-subdomain credit and returns the hostname to the pool. Invalidates
 * domain context so the picker's existing-sites list + credits refresh.
 */
export function useReleaseDomain(options?: ReleaseDomainMutationOptions) {
  const service = useDomainsService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ domainId }: ReleaseDomainVariables) => {
      return service.destroy(domainId);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: domainContextKeys.context(variables.websiteId),
      });
    },
    ...options,
  });
}

// ============================================================================
// Domain Assignment Hook (with debounce + cancel pattern)
// ============================================================================

export interface DomainAssignmentVariables {
  domain: string;
  websiteId: number;
  isPlatformSubdomain?: boolean;
}

/**
 * Hook for assigning a domain + path to a website with debounce and cancellation.
 * Uses useLatestMutation to:
 * - Debounce rapid changes (e.g., while typing path)
 * - Cancel stale in-flight requests when a new one is made
 * - Provide immediate save via mutateNowAsync (for explicit save button)
 *
 * @param websiteId - The website to assign the domain to
 * @param debounceMs - Debounce delay in ms (default: 750ms)
 */
export function useDomainAssignment(websiteId: number | undefined, debounceMs: number = 750) {
  const service = useDomainsService();
  const queryClient = useQueryClient();

  const updateCache = useCallback(
    (data: CreateDomainResponse, variables: DomainAssignmentVariables) => {
      if (!websiteId) return;
      const queryKey = domainContextKeys.context(websiteId);

      queryClient.setQueryData<GetDomainContextResponse>(queryKey, (oldData) => {
        if (!oldData) return oldData;

        const isPlatformSubdomain =
          variables.isPlatformSubdomain ?? isPlatformDomain(variables.domain);

        return {
          ...oldData,
          platform_subdomain_credits:
            data.platform_subdomain_credits as GetDomainContextResponse["platform_subdomain_credits"],
          assigned_url: {
            id: data.domain.id,
            domain_id: data.domain.id,
            domain: data.domain.domain,
            path: "/",
            is_platform_subdomain: isPlatformSubdomain,
            dns_verification_status: isPlatformSubdomain ? "verified" : "pending",
            full_url: data.domain.full_url,
          },
        };
      });

      queryClient.invalidateQueries({ queryKey });
    },
    [websiteId, queryClient]
  );

  return useLatestMutation<CreateDomainResponse, DomainAssignmentVariables>({
    mutationKey: ["domain-assignment", websiteId],
    mutationFn: async (variables: DomainAssignmentVariables, signal: AbortSignal) => {
      return service.create(
        {
          domain: variables.domain,
          website_id: variables.websiteId,
          is_platform_subdomain:
            variables.isPlatformSubdomain ?? isPlatformDomain(variables.domain),
        } as Parameters<typeof service.create>[0],
        { signal }
      );
    },
    debounceMs,
    onSuccess: updateCache,
  });
}
